import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../hooks/useAuth';
import {
  applyDraftInitiatives,
  createEncounterEntity,
  getDeadNonPlayers,
  getMainListEntities,
  getNextTurnState,
  getNormalizedCurrentEntityId,
  hydrateEncounterState,
  serializeEncounter,
} from '../utils/encounterState';
import { listEncounters, loadEncounter, saveEncounter } from '../services/encounters';

const MONSTERS_BUCKET = 'Monsters';
const PLAYERS_BUCKET = 'Players';

function rollD20() {
  return Math.floor(Math.random() * 20) + 1;
}

function getDexMod(abilities) {
  const dex = parseInt(abilities?.dexterity, 10);
  return isNaN(dex) ? 0 : Math.floor((dex - 10) / 2);
}

function parseModifier(val) {
  if (val == null) return 0;
  const s = String(val).trim();
  if (s.startsWith('+')) return parseInt(s.slice(1), 10) || 0;
  return parseInt(s, 10) || 0;
}

function rollDamage(damageStr, damageType) {
  const s = String(damageStr || '').trim();
  const diceMatch = s.match(/^(\d+)d(\d+)(?:\s*\+\s*(\d+))?$/);
  if (diceMatch) {
    const num = parseInt(diceMatch[1], 10);
    const sides = parseInt(diceMatch[2], 10);
    const mod = diceMatch[3] ? parseInt(diceMatch[3], 10) : 0;
    const rollDice = (n) =>
      Array.from({ length: n }, () => Math.floor(Math.random() * sides) + 1).reduce((a, b) => a + b, 0) + mod;
    return { damageRoll: rollDice(num), criticalRoll: rollDice(num * 2), damageType: damageType || '' };
  }
  if (/^\d+$/.test(s)) {
    const flat = parseInt(s, 10);
    return { damageRoll: flat, criticalRoll: flat * 2, damageType: damageType || '' };
  }
  return null;
}

function Encounter() {
  const { user, loading, signInWithGoogle } = useAuth();
  const [entities, setEntities] = useState([]);
  const [currentEntityId, setCurrentEntityId] = useState(null);
  const [round, setRound] = useState(1);
  const [monsterFiles, setMonsterFiles] = useState([]);
  const [addMonsterSearch, setAddMonsterSearch] = useState('');
  const [addMonsterFile, setAddMonsterFile] = useState('');
  const [addMonsterCount, setAddMonsterCount] = useState(1);
  const [playerFiles, setPlayerFiles] = useState([]);
  const [addPlayerSearch, setAddPlayerSearch] = useState('');
  const [addPlayerFile, setAddPlayerFile] = useState('');
  const [addPlayerInit, setAddPlayerInit] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerHp, setPlayerHp] = useState('');
  const [playerAc, setPlayerAc] = useState('');
  const [playerInit, setPlayerInit] = useState('');
  const [rollResult, setRollResult] = useState(null);
  const [damageTargetId, setDamageTargetId] = useState(null);
  const [attackerId, setAttackerId] = useState(null);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [expandedEntities, setExpandedEntities] = useState(new Set());
  const [draftInitiatives, setDraftInitiatives] = useState({});
  const [dmgAmount, setDmgAmount] = useState({});
  const [healAmount, setHealAmount] = useState({});
  const [encounterName, setEncounterName] = useState('');
  const [savedEncounters, setSavedEncounters] = useState([]);
  const [selectedEncounterId, setSelectedEncounterId] = useState(null);
  const [encounterBusy, setEncounterBusy] = useState(false);
  const [encounterMessage, setEncounterMessage] = useState('');
  const nextEntityNumberRef = useRef(0);
  const nextSortOrderRef = useRef(0);

  const mainListEntities = getMainListEntities(entities);
  const deadNonPlayers = getDeadNonPlayers(entities);
  const currentEntity = entities.find((entity) => entity.id === currentEntityId) || null;
  const topEntity = mainListEntities[0] || null;
  const selectedEncounter = savedEncounters.find((encounter) => encounter.id === selectedEncounterId) || null;

  const SAVE_LABELS = {
    str: 'STR', strength: 'STR',
    dex: 'DEX', dexterity: 'DEX',
    con: 'CON', constitution: 'CON',
    int: 'INT', intelligence: 'INT',
    wis: 'WIS', wisdom: 'WIS',
    cha: 'CHA', charisma: 'CHA',
  };

  const getSaveLabel = (stat) => SAVE_LABELS[String(stat).toLowerCase()] || String(stat).toUpperCase().slice(0, 3);

  const filteredMonsterFiles = addMonsterSearch.trim()
    ? monsterFiles.filter((f) => f.replace('.json', '').toLowerCase().includes(addMonsterSearch.toLowerCase()))
    : monsterFiles;
  const filteredPlayerFiles = addPlayerSearch.trim()
    ? playerFiles.filter((f) => f.replace('.json', '').toLowerCase().includes(addPlayerSearch.toLowerCase()))
    : playerFiles;

  const makeEntityId = (prefix = 'entity') => {
    nextEntityNumberRef.current += 1;
    return `${prefix}-${Date.now()}-${nextEntityNumberRef.current}`;
  };

  const buildEntity = (baseEntity, options = {}) =>
    createEncounterEntity(baseEntity, {
      id: makeEntityId(options.sourceType || 'entity'),
      sortOrder: nextSortOrderRef.current++,
      ...options,
    });

  const isExpanded = (entityId) => expandedEntities.has(entityId) || entityId === currentEntityId;

  const toggleExpanded = (entityId) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
        if (editingEntityId === entityId) setEditingEntityId(null);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  const formatActionStats = (action) => {
    const parts = [];
    if (action.to_hit_bonus) parts.push(`To hit: ${action.to_hit_bonus}`);
    if (action.save_dc && String(action.save_dc).toUpperCase() !== 'NA') {
      parts.push(`DC ${action.save_dc}${action.save_stat ? ` ${action.save_stat}` : ''}`);
    }
    if (action.damage) parts.push(`${action.damage}${action.damage_type ? ` ${action.damage_type}` : ''}`);
    if (String(action.can_multiattack || '').toLowerCase() === 'true') parts.push('Multiattack');
    return parts.length ? parts.join(', ') : null;
  };

  useEffect(() => {
    const normalizedCurrentEntityId = getNormalizedCurrentEntityId(entities, currentEntityId);
    if (normalizedCurrentEntityId !== currentEntityId) {
      setCurrentEntityId(normalizedCurrentEntityId);
    }
  }, [entities, currentEntityId]);

  useEffect(() => {
    if (!currentEntityId) return;
    setExpandedEntities((prev) => new Set([...prev, currentEntityId]));
  }, [currentEntityId]);

  useEffect(() => {
    async function loadMonsters() {
      try {
        const { data, error } = await supabase.storage.from(MONSTERS_BUCKET).list('', { limit: 500 });
        if (error) console.error('[Encounter] Monster list error:', error);
        else {
          setMonsterFiles((data || []).filter((f) => f.name && f.name.toLowerCase().endsWith('.json')).map((f) => f.name));
        }
      } catch (e) {
        console.error('[Encounter] Monster load error:', e);
      }
    }

    async function loadPlayers() {
      try {
        const { data, error } = await supabase.storage.from(PLAYERS_BUCKET).list('', { limit: 500 });
        if (error) console.error('[Encounter] Player list error:', error);
        else {
          const names = (data || []).filter((f) => f.name && f.name.toLowerCase().endsWith('.json')).map((f) => f.name);
          if ((data || []).length > 0 && names.length === 0) {
            console.warn('[Encounter] Player list: raw items', data?.map((f) => f.name));
          }
          setPlayerFiles(names);
        }
      } catch (e) {
        console.error('[Encounter] Player load error:', e);
      }
    }

    loadMonsters();
    loadPlayers();
  }, []);

  useEffect(() => {
    async function loadSavedEncounters() {
      if (!user?.id) {
        setSavedEncounters([]);
        return;
      }

      try {
        const data = await listEncounters(user.id);
        setSavedEncounters(data);
      } catch (error) {
        console.error('[Encounter] Encounter list error:', error);
      }
    }

    loadSavedEncounters();
  }, [user]);

  function syncSortOrderCounter(nextEntities) {
    nextSortOrderRef.current = nextEntities.reduce((highest, entity) => {
      const sortOrder = typeof entity.sortOrder === 'number' ? entity.sortOrder : -1;
      return Math.max(highest, sortOrder);
    }, -1) + 1;
  }

  function clearTransientState() {
    setRollResult(null);
    setDamageTargetId(null);
    setAttackerId(null);
    setEditingEntityId(null);
    setDraftInitiatives({});
    setDmgAmount({});
    setHealAmount({});
    setExpandedEntities(new Set());
  }

  async function refreshSavedEncounters(preferredEncounterId = selectedEncounterId) {
    if (!user?.id) return;

    const data = await listEncounters(user.id);
    setSavedEncounters(data);

    if (preferredEncounterId && data.some((encounter) => encounter.id === preferredEncounterId)) {
      setSelectedEncounterId(preferredEncounterId);
    }
  }

  function updateEntity(entityId, updater) {
    setEntities((prev) => prev.map((entity) => (entity.id === entityId ? updater(entity) : entity)));
  }

  function getEffectiveEntities(nextEntities = entities, nextDraftInitiatives = draftInitiatives) {
    return applyDraftInitiatives(nextEntities, nextDraftInitiatives);
  }

  function flushDraftInitiatives(nextEntities = entities, nextDraftInitiatives = draftInitiatives) {
    if (!nextDraftInitiatives || Object.keys(nextDraftInitiatives).length === 0) {
      return nextEntities;
    }

    const effectiveEntities = getEffectiveEntities(nextEntities, nextDraftInitiatives);
    setEntities(effectiveEntities);
    setDraftInitiatives({});
    return effectiveEntities;
  }

  function commitInitiative(entityId) {
    if (!Object.prototype.hasOwnProperty.call(draftInitiatives, entityId)) return;

    flushDraftInitiatives();
  }

  function discardInitiativeDraft(entityId) {
    setDraftInitiatives((prev) => {
      const next = { ...prev };
      delete next[entityId];
      return next;
    });
  }

  async function handleAddMonster(event) {
    event.preventDefault();
    if (!addMonsterFile) return;

    try {
      const { data, error } = await supabase.storage.from(MONSTERS_BUCKET).download(addMonsterFile);
      if (error) throw error;

      const json = JSON.parse(await data.text());
      const hpMatch = String(json.hit_points || '').match(/\d+/);
      const hp = hpMatch ? parseInt(hpMatch[0], 10) : 10;
      const ac = parseInt(json.armor_class, 10) || 10;
      const name = json.name || 'Monster';
      const dexMod = getDexMod(json.abilities);
      const groupId = addMonsterCount > 1 ? makeEntityId('group') : null;

      const newEntities = Array.from({ length: addMonsterCount }, (_, index) =>
        buildEntity(
          {
            name: addMonsterCount > 1 ? `${name} ${index + 1}` : name,
            hp,
            current_hp: hp,
            ac,
            initiative: rollD20() + dexMod,
            actions: json.actions || [],
            spells: json.spells || [],
            legendary_actions: json.legendary_actions || [],
            saving_throws: json.saving_throws || {},
            multi_attack: json.multi_attack,
            isPlayer: false,
          },
          {
            groupId,
            groupInstance: addMonsterCount > 1 ? index + 1 : null,
            sourceFile: addMonsterFile,
            sourceType: 'monster',
          }
        )
      );

      setEntities((prev) => [...prev, ...newEntities]);
      if (!currentEntityId && newEntities[0]) setCurrentEntityId(newEntities[0].id);
      setAddMonsterFile('');
      setAddMonsterCount(1);
    } catch (error) {
      alert(error.message || 'Failed to add monster');
    }
  }

  function handleAddPlayer(event) {
    event.preventDefault();
    const hp = parseInt(playerHp, 10);
    const ac = parseInt(playerAc, 10);
    const init = parseInt(playerInit, 10);
    if (!playerName.trim() || isNaN(hp) || isNaN(ac) || isNaN(init)) return;

    const entity = buildEntity(
      {
        name: playerName.trim(),
        hp,
        current_hp: hp,
        ac,
        initiative: init,
        actions: [],
        spells: [],
        legendary_actions: [],
        saving_throws: {},
        isPlayer: true,
      },
      { sourceType: 'player' }
    );

    setEntities((prev) => [...prev, entity]);
    if (!currentEntityId) setCurrentEntityId(entity.id);
    setPlayerName('');
    setPlayerHp('');
    setPlayerAc('');
    setPlayerInit('');
  }

  async function handleImportPlayer(event) {
    event.preventDefault();
    if (!addPlayerFile) return;

    try {
      const { data, error } = await supabase.storage.from(PLAYERS_BUCKET).download(addPlayerFile);
      if (error) throw error;

      const json = JSON.parse(await data.text());
      const hp = parseInt(json.hp, 10) || 10;
      const ac = parseInt(json.ac, 10) || 10;
      const name = json.name || 'Player';
      const init = parseInt(addPlayerInit, 10);
      if (isNaN(init)) {
        alert('Enter initiative for the player.');
        return;
      }

      const entity = buildEntity(
        {
          name,
          hp,
          current_hp: hp,
          ac,
          initiative: init,
          actions: [],
          spells: [],
          legendary_actions: [],
          saving_throws: json.saving_throws || {},
          isPlayer: true,
        },
        { sourceFile: addPlayerFile, sourceType: 'player' }
      );

      setEntities((prev) => [...prev, entity]);
      if (!currentEntityId) setCurrentEntityId(entity.id);
      setAddPlayerFile('');
      setAddPlayerInit('');
    } catch (error) {
      alert(error.message || 'Failed to import player');
    }
  }

  async function handleSavePlayer(entityId) {
    const entity = entities.find((candidate) => candidate.id === entityId);
    if (!entity) return;

    try {
      const { data, error } = await supabase.functions.invoke('upload-player', {
        body: {
          player: {
            name: entity.name,
            hp: entity.hp,
            ac: entity.ac,
            saving_throws: entity.saving_throws || {},
          },
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      const { data: listData } = await supabase.storage.from(PLAYERS_BUCKET).list('', { limit: 500 });
      setPlayerFiles((listData || []).filter((file) => file.name?.endsWith('.json')).map((file) => file.name));
      alert(`Saved as ${data?.filename || 'player.json'}`);
    } catch (error) {
      alert(error.message || 'Failed to save player');
    }
  }

  async function handleSaveEncounter() {
    if (!user?.id) return;
    if (entities.length === 0) {
      alert('Add at least one entity before saving an encounter.');
      return;
    }

    const nextName = encounterName.trim() || selectedEncounter?.name || '';
    if (!nextName) {
      alert('Enter a name for the encounter before saving.');
      return;
    }

    setEncounterBusy(true);
    setEncounterMessage('');

    try {
      const effectiveEntities = flushDraftInitiatives();
      const savedEncounter = await saveEncounter({
        userId: user.id,
        encounterId: selectedEncounterId,
        name: nextName,
        payload: serializeEncounter({ entities: effectiveEntities, round, currentEntityId }),
      });

      setSelectedEncounterId(savedEncounter.id);
      setEncounterName(savedEncounter.name);
      await refreshSavedEncounters(savedEncounter.id);
      setEncounterMessage(`Saved ${savedEncounter.name}.`);
    } catch (error) {
      console.error('[Encounter] Encounter save error:', error);
      alert(error.message || 'Failed to save encounter');
    } finally {
      setEncounterBusy(false);
    }
  }

  async function handleLoadEncounter(encounterId = selectedEncounterId) {
    if (!user?.id || !encounterId) return;

    setEncounterBusy(true);
    setEncounterMessage('');

    try {
      const savedEncounter = await loadEncounter(user.id, encounterId);
      const hydrated = hydrateEncounterState(savedEncounter.payload);

      setEntities(hydrated.entities);
      syncSortOrderCounter(hydrated.entities);
      setRound(hydrated.round);
      setCurrentEntityId(hydrated.currentEntityId);
      setSelectedEncounterId(savedEncounter.id);
      setEncounterName(savedEncounter.name);
      clearTransientState();
      setExpandedEntities(hydrated.currentEntityId ? new Set([hydrated.currentEntityId]) : new Set());
      setEncounterMessage(`Loaded ${savedEncounter.name}.`);
    } catch (error) {
      console.error('[Encounter] Encounter load error:', error);
      alert(error.message || 'Failed to load encounter');
    } finally {
      setEncounterBusy(false);
    }
  }

  function handleClearEncounter() {
    setEntities([]);
    setRound(1);
    setCurrentEntityId(null);
    setSelectedEncounterId(null);
    setEncounterName('');
    setEncounterMessage('');
    clearTransientState();
  }

  function nextTurn() {
    const effectiveEntities = flushDraftInitiatives();
    const nextTurnState = getNextTurnState(effectiveEntities, currentEntityId, round);
    setCurrentEntityId(nextTurnState.currentEntityId);
    setRound(nextTurnState.round);
  }

  function applyDamage(entityId, amount) {
    const n = parseInt(amount, 10);
    if (isNaN(n)) return;

    updateEntity(entityId, (entity) => ({
      ...entity,
      current_hp: Math.max(0, (entity.current_hp || entity.hp) - n),
    }));
    setDmgAmount((prev) => ({ ...prev, [entityId]: '' }));
  }

  function applyHeal(entityId, amount) {
    const n = parseInt(amount, 10);
    if (isNaN(n)) return;

    updateEntity(entityId, (entity) => ({
      ...entity,
      current_hp: Math.min(entity.hp || entity.current_hp, (entity.current_hp || entity.hp) + n),
    }));
    setHealAmount((prev) => ({ ...prev, [entityId]: '' }));
  }

  function doRoll(label, modifier) {
    const roll = rollD20();
    const mod = typeof modifier === 'number' ? modifier : parseModifier(modifier);
    const total = roll + mod;
    setRollResult({ label, roll, modifier: mod, total });
    setDamageTargetId(null);
  }

  function rollAction(entityId, action) {
    const toHit = rollD20();
    const mod = parseModifier(action.to_hit_bonus);
    const dmg =
      action.damage && action.damage_type && String(action.damage).toUpperCase() !== 'NA'
        ? rollDamage(action.damage, action.damage_type)
        : null;

    setAttackerId(entityId);
    const targetIds = entities.map((entity) => entity.id).filter((candidateId) => candidateId !== entityId);
    setDamageTargetId(dmg && targetIds.length > 0 ? targetIds[0] : null);
    setRollResult({
      label: action.name,
      toHit: { roll: toHit, modifier: mod, total: toHit + mod },
      damage: dmg,
      damageFormula: dmg ? { damage: action.damage, damageType: action.damage_type } : null,
      saveDc: action.save_dc,
      saveStat: action.save_stat,
    });
  }

  function rollSpell(entityId, spell) {
    const r = spell.to_hit_bonus ? rollD20() : null;
    const mod = spell.to_hit_bonus ? parseModifier(spell.to_hit_bonus) : 0;
    const dmg =
      spell.damage && spell.damage_type && String(spell.damage).toUpperCase() !== 'NA'
        ? rollDamage(spell.damage, spell.damage_type)
        : null;

    setAttackerId(entityId);
    const targetIds = entities.map((entity) => entity.id).filter((candidateId) => candidateId !== entityId);
    setDamageTargetId(dmg && targetIds.length > 0 ? targetIds[0] : null);
    setRollResult({
      label: spell.name,
      toHit: r != null ? { roll: r, modifier: mod, total: r + mod } : null,
      damage: dmg,
      damageFormula: dmg ? { damage: spell.damage, damageType: spell.damage_type } : null,
      saveDc: spell.save_dc,
      saveStat: spell.save_stat,
    });
  }

  function rollLegendary(entityId, action) {
    const r = action.to_hit_bonus ? rollD20() : null;
    const mod = action.to_hit_bonus ? parseModifier(action.to_hit_bonus) : 0;
    const dmg =
      action.damage && action.damage_type && String(action.damage).toUpperCase() !== 'NA'
        ? rollDamage(action.damage, action.damage_type)
        : null;

    setAttackerId(entityId);
    const targetIds = entities.map((entity) => entity.id).filter((candidateId) => candidateId !== entityId);
    setDamageTargetId(dmg && targetIds.length > 0 ? targetIds[0] : null);
    setRollResult({
      label: action.name,
      toHit: r != null ? { roll: r, modifier: mod, total: r + mod } : null,
      damage: dmg,
      damageFormula: dmg ? { damage: action.damage, damageType: action.damage_type } : null,
      saveDc: action.save_dc,
    });
  }

  function applyRolledDamage(targetEntityId, isCritical = false) {
    if (!rollResult?.damage) return;
    const amount = isCritical ? rollResult.damage.criticalRoll : rollResult.damage.damageRoll;
    applyDamage(targetEntityId, String(amount));
    setDamageTargetId(null);
    setAttackerId(null);
    setRollResult((prev) => (prev?.damage ? { ...prev, damage: null } : prev));
  }

  if (!loading && !user) {
    return (
      <section className="section">
        <div className="container">
          <div className="notification is-warning">
            <p className="mb-3">Sign in to run combat encounters.</p>
            <button onClick={signInWithGoogle} className="button is-primary">
              Sign in with Google
            </button>
            <Link to="/" className="button ml-2">Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="container">
        <h1 className="title">Encounter</h1>
        <p className="subtitle">Track initiative, damage, rolls, and saved encounters</p>

        <div className="columns">
          <div className="column is-one-third">
            <div className="box">
              <h3 className="title is-5">Saved Encounters</h3>
              <div className="field">
                <label className="label">Encounter Name</label>
                <div className="control">
                  <input
                    type="text"
                    className="input"
                    placeholder="Name this encounter"
                    value={encounterName}
                    onChange={(e) => setEncounterName(e.target.value)}
                  />
                </div>
              </div>
              <div className="buttons">
                <button
                  type="button"
                  className="button is-primary"
                  onClick={handleSaveEncounter}
                  disabled={encounterBusy || entities.length === 0}
                >
                  Save Encounter
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={() => handleLoadEncounter()}
                  disabled={encounterBusy || !selectedEncounterId}
                >
                  Load Selected
                </button>
                <button
                  type="button"
                  className="button is-light"
                  onClick={() => refreshSavedEncounters()}
                  disabled={encounterBusy || !user?.id}
                >
                  Refresh
                </button>
              </div>
              {encounterMessage && <p className="help is-success mb-2">{encounterMessage}</p>}
              <div className="box" style={{ maxHeight: '12rem', overflowY: 'auto', padding: '0.5rem' }}>
                {savedEncounters.length === 0 ? (
                  <p className="has-text-grey is-size-7">No saved encounters yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {savedEncounters.map((encounter) => (
                      <button
                        key={encounter.id}
                        type="button"
                        className={`button is-small is-fullwidth has-text-left ${selectedEncounterId === encounter.id ? 'is-primary' : ''}`}
                        onClick={() => {
                          setSelectedEncounterId(encounter.id);
                          setEncounterName(encounter.name);
                        }}
                      >
                        {encounter.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="help">Saved encounters are scoped to the signed-in account.</p>
            </div>

            <div className="box">
              <h3 className="title is-5">Add Monster</h3>
              <form onSubmit={handleAddMonster}>
                <div className="field">
                  <label className="label">Search</label>
                  <div className="control">
                    <input
                      type="text"
                      className="input"
                      placeholder="Type to filter monsters..."
                      value={addMonsterSearch}
                      onChange={(e) => setAddMonsterSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label">Monster</label>
                  <div className="box" style={{ maxHeight: '12rem', overflowY: 'auto', padding: '0.5rem' }}>
                    {filteredMonsterFiles.length === 0 ? (
                      <p className="has-text-grey is-size-7">
                        {addMonsterSearch.trim() ? 'No matches' : 'Type to search'}
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {filteredMonsterFiles.map((fileName) => (
                          <button
                            key={fileName}
                            type="button"
                            className={`button is-small is-fullwidth has-text-left ${addMonsterFile === fileName ? 'is-primary' : ''}`}
                            onClick={() => setAddMonsterFile(fileName)}
                          >
                            {fileName.replace('.json', '').replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="field">
                  <label className="label">Count</label>
                  <div className="control">
                    <input
                      type="number"
                      className="input"
                      value={addMonsterCount}
                      onChange={(e) => setAddMonsterCount(parseInt(e.target.value, 10) || 1)}
                      min={1}
                      max={20}
                      style={{ width: '5rem' }}
                    />
                  </div>
                </div>
                <p className="help">Initiative rolls automatically. Grouped adds keep separate saved HP per instance.</p>
                <button type="submit" className="button is-primary" disabled={!addMonsterFile}>
                  Add
                </button>
              </form>
            </div>

            <div className="box">
              <h3 className="title is-5">Import Player</h3>
              <form onSubmit={handleImportPlayer}>
                <div className="field">
                  <label className="label">Search</label>
                  <div className="control">
                    <input
                      type="text"
                      className="input"
                      placeholder="Type to filter players..."
                      value={addPlayerSearch}
                      onChange={(e) => setAddPlayerSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label">Player</label>
                  <div className="box" style={{ maxHeight: '8rem', overflowY: 'auto', padding: '0.5rem' }}>
                    {filteredPlayerFiles.length === 0 ? (
                      <p className="has-text-grey is-size-7">
                        {addPlayerSearch.trim() ? 'No matches' : 'No saved players. Add one manually and save.'}
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {filteredPlayerFiles.map((fileName) => (
                          <button
                            key={fileName}
                            type="button"
                            className={`button is-small is-fullwidth has-text-left ${addPlayerFile === fileName ? 'is-primary' : ''}`}
                            onClick={() => setAddPlayerFile(fileName)}
                          >
                            {fileName.replace('.json', '').replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="field">
                  <label className="label">Initiative</label>
                  <div className="control">
                    <input
                      type="number"
                      className="input"
                      value={addPlayerInit}
                      onChange={(e) => setAddPlayerInit(e.target.value)}
                      required
                      style={{ width: '6rem' }}
                    />
                  </div>
                </div>
                <button type="submit" className="button" disabled={!addPlayerFile || addPlayerInit.trim() === ''}>
                  Import
                </button>
              </form>
            </div>

            <div className="box">
              <h3 className="title is-5">Add Player (manual)</h3>
              <form onSubmit={handleAddPlayer}>
                <div className="field">
                  <label className="label">Name</label>
                  <div className="control">
                    <input
                      type="text"
                      className="input"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label">HP</label>
                  <div className="control">
                    <input
                      type="number"
                      className="input"
                      value={playerHp}
                      onChange={(e) => setPlayerHp(e.target.value)}
                      min={1}
                      required
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label">AC</label>
                  <div className="control">
                    <input
                      type="number"
                      className="input"
                      value={playerAc}
                      onChange={(e) => setPlayerAc(e.target.value)}
                      min={0}
                      required
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label">Initiative</label>
                  <div className="control">
                    <input
                      type="number"
                      className="input"
                      value={playerInit}
                      onChange={(e) => setPlayerInit(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="button is-primary">
                  Add
                </button>
              </form>
            </div>
          </div>

          <div className="column">
            {entities.length > 0 ? (
              <>
                <div className="level">
                  <div className="level-left">
                    <h2 className="title is-5">Round {round}</h2>
                    <p className="ml-3 mb-0 is-size-7 has-text-grey">
                      Current: <strong>{currentEntity?.name || '—'}</strong> · Top of order:{' '}
                      <strong>{topEntity?.name || '—'}</strong>
                    </p>
                    <button className="button ml-3" onClick={nextTurn}>
                      Next Turn
                    </button>
                    <button className="button is-light ml-2" onClick={handleClearEncounter}>
                      Clear
                    </button>
                  </div>
                </div>

                <div className="columns is-multiline">
                  {mainListEntities.map((entity) => (
                    <div
                      key={entity.id}
                      className={`column is-half ${entity.id === currentEntityId ? 'has-background-primary-light' : ''}`}
                    >
                      <div className="box">
                        <div
                          className="level mb-2"
                          style={!isExpanded(entity.id) ? { cursor: 'pointer', marginBottom: 0 } : {}}
                          onClick={!isExpanded(entity.id) ? () => toggleExpanded(entity.id) : undefined}
                        >
                          <div className="level-left">
                            <h3 className="title is-6 mb-0">
                              {isExpanded(entity.id) && editingEntityId === entity.id ? (
                                <input
                                  className="input is-small"
                                  style={{ width: '10rem' }}
                                  value={entity.name ?? ''}
                                  onChange={(event) => updateEntity(entity.id, (current) => ({ ...current, name: event.target.value }))}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              ) : (
                                entity.name
                              )}
                              {entity.id === currentEntityId && <span className="tag is-primary ml-1">Current</span>}
                              {entity.id === topEntity?.id && <span className="tag is-info ml-1">Top</span>}
                            </h3>
                          </div>
                          <div className="level-right">
                            {isExpanded(entity.id) ? (
                              editingEntityId === entity.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="button is-small is-success"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      commitInitiative(entity.id);
                                      setEditingEntityId(null);
                                    }}
                                  >
                                    Done
                                  </button>
                                  <button
                                    type="button"
                                    className="button is-small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      discardInitiativeDraft(entity.id);
                                      setEditingEntityId(null);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="button is-small is-light"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setEditingEntityId(entity.id);
                                      setExpandedEntities((prev) => new Set([...prev, entity.id]));
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="button is-small is-light"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSavePlayer(entity.id);
                                    }}
                                    title="Save to player library"
                                  >
                                    Save
                                  </button>
                                  {entity.id !== currentEntityId && (
                                    <button
                                      type="button"
                                      className="button is-small is-light"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        toggleExpanded(entity.id);
                                      }}
                                    >
                                      −
                                    </button>
                                  )}
                                </>
                              )
                            ) : (
                              <span className="tag is-light">▼</span>
                            )}
                          </div>
                        </div>

                        {!isExpanded(entity.id) && (
                          <p className="mb-0 is-size-7" onClick={() => toggleExpanded(entity.id)} style={{ cursor: 'pointer' }}>
                            <strong>{entity.current_hp} / {entity.hp}</strong> HP · AC {entity.ac} · Init {entity.initiative}
                          </p>
                        )}

                        {isExpanded(entity.id) && editingEntityId === entity.id ? (
                          <div className="field is-grouped is-grouped-multiline">
                            <div className="field">
                              <label className="label is-small">HP</label>
                              <input
                                type="number"
                                className="input is-small"
                                style={{ width: '4rem' }}
                                value={entity.hp ?? ''}
                                onChange={(event) => updateEntity(entity.id, (current) => ({ ...current, hp: parseInt(event.target.value, 10) || 0 }))}
                              />
                            </div>
                            <div className="field">
                              <label className="label is-small">Current HP</label>
                              <input
                                type="number"
                                className="input is-small"
                                style={{ width: '4rem' }}
                                value={entity.current_hp ?? ''}
                                onChange={(event) => updateEntity(entity.id, (current) => ({ ...current, current_hp: parseInt(event.target.value, 10) || 0 }))}
                              />
                            </div>
                            <div className="field">
                              <label className="label is-small">AC</label>
                              <input
                                type="number"
                                className="input is-small"
                                style={{ width: '4rem' }}
                                value={entity.ac ?? ''}
                                onChange={(event) => updateEntity(entity.id, (current) => ({ ...current, ac: parseInt(event.target.value, 10) || 0 }))}
                              />
                            </div>
                            <div className="field">
                              <label className="label is-small">Init</label>
                              <input
                                type="number"
                                className="input is-small"
                                style={{ width: '4rem' }}
                                value={draftInitiatives[entity.id] ?? entity.initiative ?? ''}
                                onChange={(event) => setDraftInitiatives((prev) => ({ ...prev, [entity.id]: event.target.value }))}
                                onBlur={() => commitInitiative(entity.id)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                  }
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    discardInitiativeDraft(entity.id);
                                    event.currentTarget.blur();
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <p>
                            <strong>{entity.current_hp} / {entity.hp}</strong> HP · AC {entity.ac} · Init {entity.initiative}
                          </p>
                        )}

                        {isExpanded(entity.id) && (
                          <>
                            <div className="field has-addons mt-2">
                              <div className="control">
                                <input
                                  type="number"
                                  className="input"
                                  placeholder="Dmg"
                                  value={dmgAmount[entity.id] ?? ''}
                                  onChange={(event) => setDmgAmount((prev) => ({ ...prev, [entity.id]: event.target.value }))}
                                  style={{ width: '5rem' }}
                                />
                              </div>
                              <div className="control">
                                <button type="button" className="button" onClick={() => applyDamage(entity.id, dmgAmount[entity.id])}>
                                  Damage
                                </button>
                              </div>
                              <div className="control">
                                <input
                                  type="number"
                                  className="input"
                                  placeholder="Heal"
                                  value={healAmount[entity.id] ?? ''}
                                  onChange={(event) => setHealAmount((prev) => ({ ...prev, [entity.id]: event.target.value }))}
                                  style={{ width: '5rem' }}
                                />
                              </div>
                              <div className="control">
                                <button type="button" className="button" onClick={() => applyHeal(entity.id, healAmount[entity.id])}>
                                  Heal
                                </button>
                              </div>
                            </div>

                            {entity.multi_attack && (
                              <p className="is-size-7 mb-1">
                                <strong>Multiattack:</strong> {entity.multi_attack} attack(s)
                              </p>
                            )}

                            {entity.actions?.length > 0 && (
                              <div className="mt-2">
                                <p className="is-size-7 has-text-weight-semibold mb-1">Actions</p>
                                <div className="buttons are-small">
                                  {entity.actions.map((action, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      className="button is-small mr-1 mb-1"
                                      onClick={() => rollAction(entity.id, action)}
                                      title={formatActionStats(action)}
                                    >
                                      {action.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {entity.legendary_actions?.length > 0 && (
                              <div className="mt-2">
                                <p className="is-size-7 has-text-weight-semibold mb-1">Legendary Actions</p>
                                <div className="buttons are-small">
                                  {entity.legendary_actions.map((action, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      className="button is-small mr-1 mb-1"
                                      onClick={() => rollLegendary(entity.id, action)}
                                      title={formatActionStats(action)}
                                    >
                                      {action.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {entity.spells?.length > 0 && (
                              <div className="mt-2">
                                <p className="is-size-7 has-text-weight-semibold mb-1">Spells</p>
                                <div className="buttons are-small">
                                  {entity.spells.map((spell, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      className="button is-small mr-1 mb-1"
                                      onClick={() => rollSpell(entity.id, spell)}
                                      title={formatActionStats(spell)}
                                    >
                                      {spell.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {Object.keys(entity.saving_throws || {}).length > 0 && (
                              <div className="mt-2">
                                <p className="is-size-7 has-text-weight-semibold mb-1">Saving throws:</p>
                                {Object.entries(entity.saving_throws).map(([stat, bonus]) => (
                                  <button
                                    key={stat}
                                    type="button"
                                    className="button is-small mr-1 mb-1"
                                    onClick={() => doRoll(`${getSaveLabel(stat)} Save`, bonus)}
                                    title={`${getSaveLabel(stat)} (${stat})`}
                                  >
                                    {getSaveLabel(stat)} Save
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {deadNonPlayers.length > 0 && (
                  <div className="mt-4">
                    <h4 className="title is-6 has-text-grey">Dead</h4>
                    <div className="columns is-multiline">
                      {deadNonPlayers.map((entity) => (
                        <div key={entity.id} className="column is-half">
                          <div className="box has-background-light">
                            <div className="level mb-0">
                              <div className="level-left">
                                <h3 className="title is-6 mb-0 has-text-grey">{entity.name}</h3>
                              </div>
                            </div>
                            <p className="mb-0 is-size-7 has-text-grey">
                              <strong>{entity.current_hp} / {entity.hp}</strong> HP · AC {entity.ac} · Init {entity.initiative}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {rollResult && (
                  <div className="notification is-info">
                    <strong>{rollResult.label}</strong>
                    {rollResult.toHit && (
                      <p className="mb-1">
                        To hit: d20={rollResult.toHit.roll} + {rollResult.toHit.modifier} = <strong>{rollResult.toHit.total}</strong>
                      </p>
                    )}
                    {rollResult.damage && (
                      <p className="mb-1">
                        Damage: <strong>{rollResult.damage.damageRoll}</strong> {rollResult.damage.damageType} (crit: {rollResult.damage.criticalRoll})
                      </p>
                    )}
                    {rollResult.saveDc && rollResult.saveStat && (
                      <p className="mb-1">Save DC {rollResult.saveDc} {rollResult.saveStat}</p>
                    )}
                    {!rollResult.toHit && !rollResult.damage && rollResult.roll != null && (
                      <p>d20={rollResult.roll} + {rollResult.modifier} = <strong>{rollResult.total}</strong></p>
                    )}
                    {rollResult.damage && entities.length > 1 && (
                      <div className="mt-2">
                        <p className="is-size-7 has-text-weight-semibold mb-1">Apply damage to target:</p>
                        {rollResult.damageFormula && (
                          <button
                            type="button"
                            className="button is-small is-light mr-2"
                            onClick={() => {
                              const dmg = rollDamage(
                                rollResult.damageFormula.damage,
                                rollResult.damageFormula.damageType
                              );
                              setRollResult((prev) => (prev ? { ...prev, damage: dmg } : prev));
                            }}
                          >
                            Re-roll damage
                          </button>
                        )}
                        <select
                          className="select is-small mr-2"
                          value={damageTargetId ?? ''}
                          onChange={(event) => setDamageTargetId(event.target.value === '' ? null : event.target.value)}
                        >
                          <option value="">-- Select target --</option>
                          {entities
                            .filter((entity) => !(attackerId != null && entity.id === attackerId))
                            .map((entity) => (
                              <option key={entity.id} value={entity.id}>
                                {entity.name}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="button is-small is-primary"
                          disabled={damageTargetId == null || damageTargetId === ''}
                          onClick={() => {
                            if (damageTargetId != null) applyRolledDamage(damageTargetId, false);
                          }}
                        >
                          Apply {rollResult.damage.damageRoll} damage
                        </button>
                        <button
                          type="button"
                          className="button is-small is-warning"
                          disabled={damageTargetId == null || damageTargetId === ''}
                          onClick={() => {
                            if (damageTargetId != null) applyRolledDamage(damageTargetId, true);
                          }}
                          title="Apply critical hit damage"
                        >
                          Apply critical ({rollResult.damage.criticalRoll})
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="has-text-grey">
                No entities yet. Add monsters or players above.
                <br />
                <Link to="/monsters">Browse monsters</Link> · <Link to="/monsters/create">Create monster</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Encounter;
